import { addBreadcrumb, captureException } from '@sentry/nextjs';
import { debounce } from 'lodash';
import { geoCache } from './cache';
import { AUTOCOMPLETE_CONFIG, logPaidRequest } from './config';
import { googleMapsLoader } from './googleMapsLoader';

type AutocompleteCallback = (predictions: google.maps.places.AutocompletePrediction[]) => void;

class AutocompleteManager {
  private static instance: AutocompleteManager;
  private debouncedSearch: ReturnType<typeof debounce> | null = null;
  private currentRequestId = 0;
  private activeRequest: string | null = null;

  private constructor() {}

  static getInstance(): AutocompleteManager {
    if (!AutocompleteManager.instance) {
      AutocompleteManager.instance = new AutocompleteManager();
    }
    return AutocompleteManager.instance;
  }

  /**
   * Initialize the debounced search function
   */
  initialize(debounceTime: number = AUTOCOMPLETE_CONFIG.DEFAULT_DEBOUNCE) {
    if (this.debouncedSearch) {
      this.debouncedSearch.cancel();
    }

    console.log('🔧 [AutocompleteManager] Initializing with debounce:', debounceTime);

    this.debouncedSearch = debounce(
      async (
        input: string,
        sessionId: string,
        callback: AutocompleteCallback,
        errorCallback: (error: any) => void,
      ) => {
        const requestId = ++this.currentRequestId;

        console.log('🔍 [AutocompleteManager] Debounced search executing', {
          input,
          requestId,
          debounceTime,
          timestamp: new Date().toISOString(),
        });

        try {
          // Check cache first
          const cached = geoCache.getAutocomplete(input);
          if (cached) {
            console.log('🔍 [AutocompleteManager] Cache hit, returning immediately');
            if (requestId === this.currentRequestId) {
              // Use setTimeout to ensure loading state is visible briefly
              setTimeout(() => {
                if (requestId === this.currentRequestId) {
                  callback(cached);
                }
              }, 100);
            }
            return;
          }

          // Wait for Google Maps to load (handles race condition on first page load)
          const waitStart = Date.now();
          try {
            await googleMapsLoader.ensureLoaded();
          } catch (error) {
            console.error('[AutocompleteManager] Failed waiting for Google Maps:', error);
          }
          const waitMs = Date.now() - waitStart;

          addBreadcrumb({
            category: 'autocomplete',
            message: `ensureLoaded completed in ${waitMs}ms, places available: ${!!window.google?.maps?.places}`,
            level: 'info',
            data: { waitMs, input, placesAvailable: !!window.google?.maps?.places },
          });

          // Check if Google Maps is loaded
          if (!window.google?.maps?.places) {
            console.error('[AutocompleteManager] Google Maps not loaded');
            captureException(new Error('Google Maps Places not available for autocomplete'), {
              extra: {
                waitMs,
                inputQuery: input,
                googleMapsLoaded: !!window.google?.maps,
                __googleMapsLoaded: !!(window as any).__googleMapsLoaded,
                __googleMapsError: !!(window as any).__googleMapsError,
                __googleMapsBlocked: !!(window as any).__googleMapsBlocked,
                pageUrl: typeof window !== 'undefined' ? window.location.href : 'unknown',
                userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
              },
            });
            errorCallback(new Error('Google Maps not loaded'));
            return;
          }

          // Log paid request
          logPaidRequest('Places Autocomplete', {
            input,
            sessionId,
            costEstimate: '$0.00283 per request',
            apiEndpoint: 'AutocompleteService.getPlacePredictions',
            note: 'Debounced request - only final input searched',
          });

          const autocompleteService = new window.google.maps.places.AutocompleteService();

          autocompleteService.getPlacePredictions(
            { input },
            (
              predictions: google.maps.places.AutocompletePrediction[],
              status: google.maps.places.PlacesServiceStatus,
            ) => {
              // Only process if this is still the latest request
              if (requestId !== this.currentRequestId) {
                console.log('🔍 [AutocompleteManager] Ignoring stale request', requestId);
                return;
              }

              if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
                geoCache.setAutocomplete(input, predictions);
                callback(predictions);
              } else {
                console.error('[AutocompleteManager] Autocomplete failed:', status);
                captureException(new Error('Google Maps Autocomplete API returned non-OK status'), {
                  extra: {
                    status,
                    inputQuery: input,
                    fromCache: false,
                  },
                });
                callback([]);
              }
            },
          );
        } catch (error) {
          console.error('[AutocompleteManager] Error:', error);
          errorCallback(error);
        }
      },
      debounceTime,
      {
        leading: false,
        trailing: true,
      },
    );
  }

  /**
   * Search for autocomplete predictions
   */
  search(
    input: string,
    sessionId: string,
    callback: AutocompleteCallback,
    errorCallback: (error: any) => void = () => {},
  ) {
    // Cancel any pending request
    this.currentRequestId++;

    console.log('🔍 [AutocompleteManager] Search called', {
      input,
      length: input.length,
      minLength: AUTOCOMPLETE_CONFIG.MIN_LENGTH,
      requestId: this.currentRequestId,
      timestamp: new Date().toISOString(),
      note: 'Loading skeleton should show NOW',
    });

    // Check minimum length
    if (input.trim().length < AUTOCOMPLETE_CONFIG.MIN_LENGTH) {
      console.log('🔍 [AutocompleteManager] Input too short, clearing results');
      callback([]);
      return;
    }

    // Store the active request
    this.activeRequest = input;

    // Initialize if needed
    if (!this.debouncedSearch) {
      this.initialize();
    }

    // Cancel previous debounce
    this.debouncedSearch?.cancel();

    console.log('🔍 [AutocompleteManager] Starting debounce timer', {
      input,
      willExecuteIn: `${AUTOCOMPLETE_CONFIG.DEFAULT_DEBOUNCE}ms`,
    });

    // Call the debounced function
    this.debouncedSearch!(input, sessionId, callback, errorCallback);
  }

  /**
   * Cancel any pending searches
   */
  cancel() {
    this.currentRequestId++;
    this.activeRequest = null;
    if (this.debouncedSearch) {
      this.debouncedSearch.cancel();
    }
  }

  /**
   * Get the currently active request
   */
  getActiveRequest(): string | null {
    return this.activeRequest;
  }
}

export const autocompleteManager = AutocompleteManager.getInstance();
